import React, {useState} from 'react';

import {AttachmentMedia} from 'src/components/trigger-attachment/attachment-media';
import {fileAttachmentPreview, fileAttachmentRemoveBtn} from 'src/components/trigger-attachment/trigger-attachment.module.styl';
import {MAX_ACTIVE_ATTACHMENTS_COUNT} from 'src/constants';
import {PushNotificationIds} from 'src/modules/event-create/constants';
import {
    useEventTriggerAttachment,
} from 'src/modules/event-create/header-block/event-trigger-attachment/hooks/useEventTriggerAttachment';
import {TriggerAttachment} from 'src/openapi/models/TriggerAttachment';
import {getAttachmentCreationDate} from 'src/utils/getAttachmentCreationDate';

import {Group} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

type Props = {
    attachment: TriggerAttachment,
    setFullscreen: (attachment: TriggerAttachment | null) => void,
};

const ERROR_AUTO_HIDE_TIMEOUT = 10000;
const ERROR_MESSAGE = `You can't have more than ${MAX_ACTIVE_ATTACHMENTS_COUNT} active attachments.
 Please remove some attachments to add new ones.`;

export const AttachmentPreview = (props: Props) => {
    const {
        attachment,
        setFullscreen,
    } = props;

    const {onCreateNote} = usePushNotification();
    const {removeAttachment, restoreAttachment, activeTriggerAttachmentsCount} = useEventTriggerAttachment();
    const [isActive, setIsActive] = useState<boolean>(attachment.active);

    const onRemove = () => {
        removeAttachment(attachment);
        setIsActive(false);
    };

    const onRestore = () => {
        if (activeTriggerAttachmentsCount >= MAX_ACTIVE_ATTACHMENTS_COUNT) {
            onCreateNote({
                id: PushNotificationIds.maxTriggerAttachment,
                status: Status.ERROR,
                message: ERROR_MESSAGE,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
            return;
        }

        restoreAttachment(attachment);
        setIsActive(true);
    };

    return (
        <Group direction="v" gap="xxs">
            <Group justify="center" alignItems="center">
                {isActive ? (
                    <Group gap="none" className={fileAttachmentPreview}>
                        <AttachmentMedia
                            isPreview={true}
                            attachment={attachment}
                            setFullscreen={setFullscreen}
                        />
                        <Tooltip
                            anchor={({anchorRef}) => (
                                <Group justify="center" alignItems="center">
                                    <BasicIconButton
                                        ref={anchorRef}
                                        className={fileAttachmentRemoveBtn}
                                        iconProps={{type: Icons.TRASH_LIGHT, size: Sizes.Size_19}}
                                        onClick={onRemove}
                                    />
                                </Group>
                            )}>
                            Remove
                        </Tooltip>
                    </Group>
                ) : (
                    <Tooltip
                        anchor={({anchorRef}) => (
                            <Group justify="center" alignItems="center" className={fileAttachmentPreview}>
                                <BasicIconButton
                                    ref={anchorRef}
                                    iconProps={{type: Icons.EYE_SLASH, size: Sizes.Size_19}}
                                    onClick={onRestore}
                                />
                            </Group>
                        )}>
                        Restore
                    </Tooltip>
                )}
            </Group>
            <Group justify="center">
                {getAttachmentCreationDate(attachment)}
            </Group>
        </Group>
    );
};
